<app-breadcrumb
  [title]="'aiVoiceInvoice.breadcrumbTitle' | translate"
  [items]="['aiVoiceInvoice.breadcrumbDashboard' | translate]"
  [active_item]="'aiVoiceInvoice.breadcrumbTitle' | translate"
>
</app-breadcrumb>

<div
  class="ai-voice-page"
  [attr.dir]="chatDir"
  [class.ai-voice-page--has-preview]="previewInvoiceId != null"
>
  <div class="workspace">
    <!-- Sidebar: sessions -->
    <aside class="sidebar-panel">
      <div class="sidebar-head">
        <div class="sidebar-head-title">
          <span class="sidebar-head-icon" aria-hidden="true">
            <i class="fa fa-comments-o"></i>
          </span>
          <div>
            <h3 class="sidebar-title">{{ 'aiVoiceInvoice.sidebarTitle' | translate }}</h3>
            <span class="sidebar-subtitle" *ngIf="sessionsTotal">
              {{ 'aiVoiceInvoice.sidebarCount' | translate: { n: sessionsTotal } }}
            </span>
          </div>
        </div>
        <div class="sidebar-head-actions">
          <button
            type="button"
            class="icon-btn"
            (click)="startNewChat()"
            [disabled]="isAiProcessing || isConfirming"
            [title]="'aiVoiceInvoice.newChatHint' | translate"
            [attr.aria-label]="'aiVoiceInvoice.newChat' | translate"
          >
            <i class="fa fa-plus"></i>
          </button>
          <button
            type="button"
            class="icon-btn"
            (click)="refreshSessions()"
            [title]="'aiVoiceInvoice.refreshSessions' | translate"
            [attr.aria-label]="'aiVoiceInvoice.refreshSessions' | translate"
          >
            <i class="fa fa-refresh"></i>
          </button>
        </div>
      </div>

      <div class="sidebar-list" *ngIf="sessionList.length; else emptySessions">
        <button
          type="button"
          class="session-card"
          *ngFor="let s of sessionList"
          [class.session-card--active]="s.sessionId === activeSidebarSessionId"
          [class.session-card--done]="s.status === 'completed'"
          (click)="selectSessionFromSidebar(s)"
        >
          <span class="session-avatar" aria-hidden="true">{{ sessionInitials(s) }}</span>
          <span class="session-body">
            <span class="session-row-top">
              <span class="session-row-title">{{ sessionPreviewTitle(s) }}</span>
              <span class="session-row-time" *ngIf="sessionRelativeTime(s) as t">{{ t }}</span>
            </span>
            <span class="session-row-preview" *ngIf="s.last_message_preview">
              {{ s.last_message_preview }}
            </span>
            <span class="session-row-meta">
              <span class="meta-pill" *ngIf="s.messageCount != null">
                <i class="fa fa-comment-o" aria-hidden="true"></i>
                {{ s.messageCount }} {{ 'aiVoiceInvoice.messagesShort' | translate }}
              </span>
              <span class="meta-pill meta-pill--tokens" *ngIf="s.total_llm_tokens"
                [title]="'aiVoiceInvoice.sessionTokensUsedHint' | translate">
                <i class="fa fa-bolt" aria-hidden="true"></i>
                {{ 'aiVoiceInvoice.sessionTokensUsed' | translate: { n: (s.total_llm_tokens | number) } }}
              </span>
              <span class="meta-pill meta-pill--done" *ngIf="s.status === 'completed'">
                <i class="fa fa-check" aria-hidden="true"></i>
                {{ 'aiVoiceInvoice.statusCompleted' | translate }}
              </span>
            </span>
          </span>
        </button>
      </div>

      <ng-template #emptySessions>
        <div class="sidebar-empty">
          <span class="sidebar-empty-icon" aria-hidden="true"><i class="fa fa-inbox"></i></span>
          <p class="sidebar-empty-text">{{ 'aiVoiceInvoice.sessionsEmpty' | translate }}</p>
        </div>
      </ng-template>
    </aside>

    <!-- Main -->
    <section class="main-panel">
      <header class="main-header main-header--compact">
        <div class="main-header-actions main-header-actions--full">
          <button
            type="button"
            class="btn-pill btn-pill--ghost"
            (click)="startNewChat()"
            [disabled]="isAiProcessing || isConfirming"
            [title]="'aiVoiceInvoice.newChatHint' | translate"
          >
            <i class="fa fa-plus-circle"></i>
            <span>{{ 'aiVoiceInvoice.newChat' | translate }}</span>
          </button>
          <a class="btn-pill btn-pill--link" routerLink="/dashboard/invoices-v2">
            <i class="fa fa-arrow-left"></i>
            <span>{{ 'aiVoiceInvoice.backToInvoices' | translate }}</span>
          </a>
        </div>
      </header>

      <div class="main-body">
        <div class="read-only-banner read-only-banner--inline" *ngIf="hasSession && !confirmResult && sessionIsReadOnly">
          <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
          <span class="read-only-banner__text">{{ 'aiVoiceInvoice.readOnlySession' | translate }}</span>
          <button
            type="button"
            class="read-only-banner__cta btn-pill btn-pill--ghost btn-pill--sm"
            (click)="startNewChat()"
            [disabled]="isAiProcessing || isConfirming"
          >
            <i class="fa fa-plus-circle" aria-hidden="true"></i>
            {{ 'aiVoiceInvoice.readOnlySessionBannerCta' | translate }}
          </button>
        </div>

        <!-- Chat + live invoice preview split — always visible once bootstrap loads -->
        <div
          class="workspace-split"
          *ngIf="bootstrapLoaded && !confirmResult"
          [class.workspace-split--has-preview]="previewInvoiceId != null"
          [class.workspace-split--with-guide]="previewInvoiceId == null"
        >
        <!-- Chat area -->
        <section class="chat-panel">
          <div class="chat-panel-head" *ngIf="chatMessages.length > 0">
            <h4 class="chat-panel-title">
              <i class="fa fa-comments" aria-hidden="true"></i>
              {{ 'aiVoiceInvoice.chatTitle' | translate }}
            </h4>
            <div class="chat-export-actions">
              <button
                type="button"
                class="icon-btn icon-btn--sm"
                (click)="exportChat('text')"
                [title]="'aiVoiceInvoice.exportAsText' | translate"
              >
                <i class="fa fa-file-text-o" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="icon-btn icon-btn--sm"
                (click)="exportChat('json')"
                [title]="'aiVoiceInvoice.exportAsJson' | translate"
              >
                <i class="fa fa-code" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div class="chat-stream" #chatScroll [attr.dir]="chatDir">
            <!-- Welcome: greeting + examples + quick chips -->
            <div class="chat-welcome" *ngIf="showWelcomePanel">
              <div class="msg msg--system msg--welcome">
                <div class="msg-avatar msg-avatar--system msg-avatar--zaki" aria-hidden="true">
                  <i class="fa fa-magic"></i>
                </div>
                <div class="bubble bubble--system bubble--welcome">
                  <span class="bubble-role">{{ 'aiVoiceInvoice.assistantName' | translate }}</span>
                  <p class="bubble-text">{{ 'aiVoiceInvoice.welcomeGreeting' | translate }}</p>
                </div>
              </div>

              <div class="examples-section">
                <h5 class="examples-section-title">{{ 'aiVoiceInvoice.examplesSectionTitle' | translate }}</h5>
                <div class="example-cards example-cards--horizontal">
                  <button
                    type="button"
                    class="example-card"
                    [class.example-card--mint]="ex.tone === 'mint'"
                    [class.example-card--sky]="ex.tone === 'sky'"
                    [class.example-card--peach]="ex.tone === 'peach'"
                    *ngFor="let ex of exampleCards"
                    (click)="clickExampleByKey(ex.textKey)"
                  >
                    <span class="example-card-icon" aria-hidden="true">
                      <i class="fa" [ngClass]="ex.icon"></i>
                    </span>
                    <span class="example-card-body">
                      <span class="example-card-title">{{ ex.titleKey | translate }}</span>
                      <span class="example-card-text">{{ ex.textKey | translate }}</span>
                    </span>
                  </button>
                </div>
              </div>

              <div class="doc-suggestions doc-suggestions--welcome" *ngIf="showDocSuggestions" [dir]="chatDir">
                <div class="doc-suggestions-row">
                  <button
                    *ngFor="let chip of docSuggestionChips"
                    type="button"
                    class="doc-suggestion-chip"
                    (click)="applyDocSuggestion(chip.template)"
                  >
                    <i class="fa" [ngClass]="chip.icon" aria-hidden="true"></i>
                    {{ chip.label }}
                  </button>
                </div>
              </div>
            </div>

            <ng-container *ngFor="let m of chatMessages; trackBy: trackByMessageId">
              <!-- Merchant bubble -->
              <div *ngIf="m.role === 'merchant'" class="msg msg--merchant">
                <div class="msg-avatar msg-avatar--merchant msg-avatar--initials" aria-hidden="true">
                  {{ merchantInitials }}
                </div>
                <div
                  class="bubble bubble--merchant"
                  [class.bubble--merchant-image]="m.merchantImageUpload"
                >
                  <ng-container *ngIf="m.merchantImageUpload; else plainMerchantText">
                    <span class="bubble-image-label">
                      <i class="fa fa-paperclip" aria-hidden="true"></i>
                      <span class="bubble-image-filename">{{ m.uploadedFileName }}</span>
                    </span>
                    <p class="bubble-text" *ngIf="m.text !== m.uploadedFileName">{{ m.text }}</p>
                  </ng-container>
                  <ng-template #plainMerchantText>
                    <p class="bubble-text">{{ m.text }}</p>
                  </ng-template>
                </div>
              </div>

              <!-- Assistant bubble -->
              <div
                *ngIf="m.role === 'system'"
                class="msg msg--system"
                [class.msg--wide]="!!m.reportSafeHtml"
              >
                <div class="msg-avatar msg-avatar--system msg-avatar--zaki" aria-hidden="true">
                  <i class="fa fa-magic"></i>
                </div>
                <div
                  class="bubble bubble--system"
                  [ngClass]="
                    m.isConfirmSuccess
                      ? 'bubble--confirm-ok'
                      : m.setupStep
                      ? 'bubble--setup'
                      : bubbleClassForStatus(m.voiceResponse?.status || 'error')
                  "
                >
                  <span class="bubble-role">{{ 'aiVoiceInvoice.assistantName' | translate }}</span>
                  <p class="bubble-text">{{ m.text }}</p>

                  <div
                    class="quick-reply-actions"
                    *ngIf="getQuickReplyButtons(m).length && !m.isConfirmSuccess && !m.setupStep"
                  >
                    <button
                      *ngFor="let btn of getQuickReplyButtons(m)"
                      type="button"
                      class="quick-reply-btn"
                      [class.quick-reply-btn--primary]="btn.variant === 'primary'"
                      [class.quick-reply-btn--secondary]="btn.variant === 'secondary'"
                      (click)="sendQuickReply(m, btn)"
                      [disabled]="!canInteractWithQuickReplies(m) || !canSendMessages || isAiProcessing"
                    >
                      <i class="fa" *ngIf="btn.icon" [ngClass]="btn.icon" aria-hidden="true"></i>
                      {{ btn.labelKey | translate }}
                    </button>
                  </div>

                  <!-- Onboarding setup chips — company selection (multi-company only) -->
                  <div class="setup-chips" *ngIf="m.setupStep === 'company' && !m.setupCompleted">
                    <button
                      *ngFor="let c of companies"
                      type="button"
                      class="setup-chip"
                      (click)="selectSetupCompany(c)"
                    >
                      <i class="fa fa-building-o" aria-hidden="true"></i>
                      {{ c.label || c.name }}
                    </button>
                  </div>

                  <!-- Report HTML + export + diagonal watermark -->
                  <div *ngIf="m.reportSafeHtml" class="report-export-shell" [dir]="chatDir">
                    <div class="report-export-toolbar">
                      <span class="report-export-brand">{{
                        'aiVoiceInvoice.reportExportBadge' | translate
                      }}</span>
                      <div class="report-export-actions">
                        <button
                          type="button"
                          class="report-export-btn"
                          (click)="downloadReportExcel(m); $event.stopPropagation()"
                          [disabled]="!reportExportHasData(m)"
                          [title]="'aiVoiceInvoice.reportDownloadExcel' | translate"
                        >
                          <i class="fa fa-file-excel-o" aria-hidden="true"></i>
                          {{ 'aiVoiceInvoice.reportDownloadExcel' | translate }}
                        </button>
                      </div>
                    </div>
                    <div class="report-export-body">
                      <div class="report-export-watermark" aria-hidden="true">
                        <span>{{ 'aiVoiceInvoice.reportWatermark' | translate }}</span>
                      </div>
                      <div class="report-html-wrap" [innerHTML]="m.reportSafeHtml"></div>
                    </div>
                  </div>

                  <p class="resolved-line" *ngIf="customerResolvedLine(m) && !m.isConfirmSuccess && !m.setupStep && !canShowUnderstoodSummary(m)">
                    <i class="fa fa-check-circle" aria-hidden="true"></i>
                    {{ customerResolvedLine(m) }}
                  </p>

                  <!-- Extracted data summary card -->
                  <div class="understood-card" *ngIf="canShowUnderstoodSummary(m)">
                    <h5 class="understood-card-title">
                      <i class="fa fa-list-alt" aria-hidden="true"></i>
                      {{ 'aiVoiceInvoice.understoodTitle' | translate }}
                    </h5>
                    <div class="understood-grid">
                      <div
                        class="understood-row"
                        *ngFor="let row of buildUnderstoodSummary(m)"
                        [class.understood-row--missing]="row.status === 'missing'"
                        [class.understood-row--warn]="row.status === 'warn'"
                      >
                        <span class="understood-row-icon" [ngClass]="'understood-row-icon--' + row.status">
                          <i class="fa" [ngClass]="row.icon" aria-hidden="true"></i>
                        </span>
                        <div class="understood-row-body">
                          <span class="understood-row-label">{{ row.labelKey | translate }}</span>
                          <span class="understood-row-value">{{ row.value }}</span>
                        </div>
                        <span class="understood-row-status" *ngIf="row.status !== 'ok'" aria-hidden="true">
                          <i class="fa" [ngClass]="row.status === 'missing' ? 'fa-times-circle' : 'fa-exclamation-circle'"></i>
                        </span>
                      </div>
                    </div>
                    <div class="understood-actions">
                      <button
                        type="button"
                        class="btn-understood-create"
                        *ngIf="canShowConfirmSubmitButton(m) || canShowSaveDraftButton(m)"
                        (click)="canShowConfirmSubmitButton(m) ? confirmInvoice(true) : confirmInvoice(false)"
                        [disabled]="isConfirming"
                      >
                        <i class="fa fa-plus-circle" aria-hidden="true"></i>
                        {{ 'aiVoiceInvoice.createDocument' | translate }}
                      </button>
                      <button
                        type="button"
                        class="btn-understood-edit"
                        (click)="focusComposerForEdit()"
                        [disabled]="isAiProcessing"
                      >
                        <i class="fa fa-pencil" aria-hidden="true"></i>
                        {{ 'aiVoiceInvoice.editData' | translate }}
                      </button>
                    </div>
                  </div>

                  <!-- Inline invoice result -->
                  <div
                    class="invoice-inline"
                    *ngIf="m.voiceResponse?.invoice_result && !m.isConfirmSuccess && !m.setupStep"
                  >
                    <div
                      class="invoice-inline-error"
                      role="alert"
                      *ngIf="m.voiceResponse.invoice_result.error"
                    >
                      <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
                      <span>{{ m.voiceResponse.invoice_result.error }}</span>
                    </div>
                    <div
                      class="invoice-inline-ok"
                      *ngIf="!m.voiceResponse.invoice_result.error && (m.voiceResponse.invoice_result.pdf_link || m.voiceResponse.invoice_result.document_number)"
                    >
                      <span *ngIf="m.voiceResponse.invoice_result.document_number" class="invoice-inline-number">
                        <i class="fa fa-hashtag" aria-hidden="true"></i>
                        {{ m.voiceResponse.invoice_result.document_number }}
                      </span>
                      <a
                        *ngIf="m.voiceResponse.invoice_result.pdf_link"
                        [href]="m.voiceResponse.invoice_result.pdf_link"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="invoice-inline-pdf"
                      >
                        <i class="fa fa-file-pdf-o" aria-hidden="true"></i>
                        {{ 'aiVoiceInvoice.openPdf' | translate }}
                      </a>
                    </div>
                    <button
                      type="button"
                      class="btn-pill btn-pill--primary btn-pill--sm"
                      *ngIf="!m.voiceResponse.invoice_result.error && m.voiceResponse.invoice_result.invoice_id != null"
                      (click)="goToInvoiceView(m.voiceResponse.invoice_result.invoice_id)"
                    >
                      <i class="fa fa-external-link" aria-hidden="true"></i>
                      {{ 'aiVoiceInvoice.openInvoice' | translate }}
                    </button>
                  </div>

                  <ng-container *ngIf="m.voiceResponse && !m.isConfirmSuccess && !m.setupStep">
                    <div *ngIf="m.voiceResponse.options?.length" class="options-block">
                      <ng-container
                        *ngIf="m.voiceResponse.multi_select && m.voiceResponse.status === 'need_selection'; else singleOptionList"
                      >
                        <p class="options-title">{{ 'aiVoiceInvoice.optionsTitleMulti' | translate }}</p>
                        <ul class="option-multi">
                          <li *ngFor="let o of m.voiceResponse.options">
                            <label
                              class="option-multi-row"
                              [class.option-multi-row--disabled]="!canInteractWithOptions(m)"
                            >
                              <input
                                type="checkbox"
                                [checked]="isMultiOptionSelected(o.id)"
                                (change)="toggleMultiOption(m, o.id, $any($event.target).checked)"
                                [disabled]="!canInteractWithOptions(m) || !canSendMessages || isAiProcessing"
                              />
                              <span class="option-kind option-kind--{{ o.kind }}">{{ o.kind }}</span>
                              <span class="option-label">{{ o.label }}</span>
                            </label>
                          </li>
                        </ul>
                        <div class="options-footer">
                          <button
                            type="button"
                            class="btn-pill btn-pill--primary btn-pill--sm"
                            (click)="submitMultiSelection()"
                            [disabled]="
                              !canInteractWithOptions(m) || !canSendMessages || isAiProcessing || pendingMultiSelectEmpty()
                            "
                          >
                            <i class="fa fa-check"></i>
                            {{ 'aiVoiceInvoice.multiSelectApply' | translate }}
                          </button>
                          <span class="options-disclaimer">
                            {{ 'aiVoiceInvoice.optionsDisclaimer' | translate }}
                          </span>
                        </div>
                      </ng-container>
                      <ng-template #singleOptionList>
                        <p class="options-title">{{ 'aiVoiceInvoice.optionsTitle' | translate }}</p>
                        <ul class="option-single">
                          <li *ngFor="let o of m.voiceResponse.options">
                            <button
                              type="button"
                              class="option-btn"
                              (click)="selectOption(m, o)"
                              [disabled]="!canInteractWithOptions(m) || !canSendMessages || isAiProcessing"
                            >
                              <span class="option-kind option-kind--{{ o.kind }}">{{ o.kind }}</span>
                              <span class="option-label">{{ o.label }}</span>
                            </button>
                          </li>
                        </ul>
                        <p class="options-disclaimer" *ngIf="m.voiceResponse.status === 'need_selection'">
                          {{ 'aiVoiceInvoice.optionsDisclaimer' | translate }}
                        </p>
                      </ng-template>
                    </div>
                  </ng-container>

                  <!-- Confirm actions (fallback when summary card is hidden) -->
                  <div class="confirm-actions" *ngIf="canShowConfirmActionsOnMessage(m) && !canShowUnderstoodSummary(m)">
                    <p
                      class="confirm-hint"
                      *ngIf="canShowConfirmSubmitButton(m) || canShowSaveDraftButton(m)"
                    >
                      {{ 'aiVoiceInvoice.confirmHint' | translate }}
                    </p>
                    <div class="confirm-buttons">
                      <button
                        type="button"
                        class="btn-pill btn-pill--success"
                        *ngIf="canShowConfirmSubmitButton(m)"
                        (click)="confirmInvoice(true)"
                        [disabled]="isConfirming"
                      >
                        <i class="fa fa-paper-plane"></i>
                        {{ 'aiVoiceInvoice.confirmAndSubmit' | translate }}
                      </button>
                      <button
                        type="button"
                        class="btn-pill btn-pill--ghost"
                        *ngIf="canShowSaveDraftButton(m)"
                        (click)="confirmInvoice(false)"
                        [disabled]="isConfirming"
                      >
                        <i class="fa fa-floppy-o"></i>
                        {{ 'aiVoiceInvoice.saveDraft' | translate }}
                      </button>
                    </div>
                  </div>

                  <div
                    class="confirm-open"
                    *ngIf="m.isConfirmSuccess && m.confirmedInvoiceId != null"
                  >
                    <button
                      type="button"
                      class="btn-pill btn-pill--primary btn-pill--sm"
                      (click)="goToInvoiceView(m.confirmedInvoiceId)"
                    >
                      <i class="fa fa-external-link" aria-hidden="true"></i>
                      {{ 'aiVoiceInvoice.openInvoice' | translate }}
                    </button>
                  </div>
                </div>
              </div>
            </ng-container>

            <!-- Typing indicator -->
            <div *ngIf="isAiProcessing" class="msg msg--system">
              <div class="msg-avatar msg-avatar--system" aria-hidden="true">
                <i class="fa fa-magic"></i>
              </div>
              <div class="bubble bubble--system bubble--typing">
                <span class="bubble-role">{{ 'aiVoiceInvoice.assistantName' | translate }}</span>
                <div class="thinking-row" aria-busy="true">
                  <span class="thinking-label">
                    {{ 'aiVoiceInvoice.thinking' | translate
                    }}<span class="thinking-dot thinking-dot--1">.</span><span
                      class="thinking-dot thinking-dot--2"
                      >.</span
                    ><span class="thinking-dot thinking-dot--3">.</span>
                  </span>
                  <div class="typing-dots">
                    <span></span><span></span><span></span>
                  </div>
                </div>
                <span class="sr-only">{{ 'aiVoiceInvoice.processing' | translate }}</span>
              </div>
            </div>
          </div>

          <!-- Report mode suggestion chips -->
          <div
            *ngIf="showReportSuggestions && !isAiProcessing"
            class="report-suggestions"
            [dir]="chatDir"
          >
            <button
              *ngFor="let prompt of reportSuggestionChips"
              type="button"
              class="report-suggestion-chip"
              (click)="sendReportSuggestion(prompt)"
            >
              <i class="fa fa-bar-chart" aria-hidden="true"></i>
              {{ prompt }}
            </button>
          </div>

          <!-- Composer — visible from the start; triggers onboarding when no session yet -->
          <div class="composer">
            <!-- Hidden file input for image upload -->
            <input
              #imageInput
              type="file"
              class="composer-file-input"
              [accept]="acceptedImageTypes"
              (change)="onImageFileSelected($event)"
            />

            <div
              class="composer-shell"
              [class.composer-shell--recording]="isRecording"
              [class.composer-shell--disabled]="isAiProcessing"
              [class.composer-shell--readonly]="sessionIsReadOnly && !isAiProcessing"
            >
              <!-- Image upload button -->
              <button
                type="button"
                class="composer-attach"
                [class.composer-attach--uploading]="isUploadingImage"
                (click)="triggerImageUpload()"
                [disabled]="isAiProcessing || (hasSession && !canSendMessages)"
                [title]="'aiVoiceInvoice.uploadImage' | translate"
                [attr.aria-label]="'aiVoiceInvoice.uploadImage' | translate"
              >
                <i
                  class="fa"
                  [ngClass]="isUploadingImage ? 'fa-spinner fa-spin' : 'fa-paperclip'"
                ></i>
              </button>

              <button
                type="button"
                class="composer-mic"
                [class.composer-mic--active]="isRecording"
                [class.composer-mic--disabled]="!supportsBrowserStt"
                (click)="toggleRecording()"
                [disabled]="isAiProcessing || (hasSession && !canSendMessages)"
                [title]="
                  (!supportsBrowserStt
                    ? 'aiVoiceInvoice.sttUnsupported'
                    : isRecording
                    ? 'aiVoiceInvoice.stopAndSend'
                    : 'aiVoiceInvoice.startRecording'
                  ) | translate
                "
              >
                <i class="fa" [ngClass]="isRecording ? 'fa-stop' : 'fa-microphone'"></i>
                <span class="mic-pulse" *ngIf="isRecording" aria-hidden="true"></span>
              </button>

              <textarea
                #composerTextarea
                class="composer-input"
                [(ngModel)]="draftText"
                rows="1"
                [disabled]="isAiProcessing"
                [placeholder]="
                  sessionIsReadOnly
                    ? ('aiVoiceInvoice.textPlaceholderReadOnly' | translate)
                    : (
                        (!hasSession
                          ? 'aiVoiceInvoice.textPlaceholderNoSession'
                          : 'aiVoiceInvoice.textPlaceholder'
                        ) | translate
                      )
                "
                (keydown)="onComposerKeydown($event)"
              ></textarea>

              <button
                type="button"
                class="composer-send"
                (click)="sendTextMessage()"
                [disabled]="isAiProcessing || !draftText?.trim()"
                [title]="'aiVoiceInvoice.sendText' | translate"
                [attr.aria-label]="'aiVoiceInvoice.sendText' | translate"
              >
                <i class="fa fa-paper-plane"></i>
              </button>
            </div>

            <div class="composer-foot">
              <span class="composer-tip" *ngIf="sessionIsReadOnly; else composerKeyboardTip">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                {{ 'aiVoiceInvoice.composerReadOnlyHint' | translate }}
              </span>
              <ng-template #composerKeyboardTip>
                <span class="composer-tip">
                  <i class="fa fa-keyboard-o" aria-hidden="true"></i>
                  {{
                    (onboardingStep === 'company'
                      ? 'aiVoiceInvoice.composerTipPickCompany'
                      : 'aiVoiceInvoice.composerTip'
                    ) | translate
                  }}
                </span>
              </ng-template>
              <span class="composer-warn" *ngIf="!supportsBrowserStt">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                {{ 'aiVoiceInvoice.sttUnsupported' | translate }}
              </span>
              <span class="composer-warn composer-warn--error" *ngIf="micError">{{ micError }}</span>
            </div>
          </div>
        </section>

        <!-- How it works — right guide panel -->
        <aside class="guide-panel" *ngIf="previewInvoiceId == null" aria-label="Guide">
          <h4 class="guide-panel-title">{{ 'aiVoiceInvoice.guideTitle' | translate }}</h4>
          <ol class="guide-steps">
            <li class="guide-step" *ngFor="let step of guideSteps; let i = index">
              <span class="guide-step-num">{{ i + 1 }}</span>
              <span class="guide-step-icon" aria-hidden="true">
                <i class="fa" [ngClass]="step.icon"></i>
              </span>
              <div class="guide-step-body">
                <span class="guide-step-title">{{ step.titleKey | translate }}</span>
                <p class="guide-step-desc">{{ step.descKey | translate }}</p>
              </div>
            </li>
          </ol>
          <div class="guide-security">
            <i class="fa fa-lock" aria-hidden="true"></i>
            <p>{{ 'aiVoiceInvoice.guideSecurity' | translate }}</p>
          </div>
        </aside>

        <!-- Live invoice preview — appears with a slide-in transition the moment an invoice id arrives -->
        <aside class="preview-panel" *ngIf="previewInvoiceId != null" aria-live="polite">
          <header class="preview-head">
            <div class="preview-head-title">
              <span class="preview-head-icon" aria-hidden="true">
                <i class="fa fa-file-text-o"></i>
              </span>
              <div class="preview-head-text">
                <h4 class="preview-title">{{ 'aiVoiceInvoice.previewTitle' | translate }}</h4>
                <span class="preview-sub">
                  <span class="preview-id">#{{ previewInvoiceId }}</span>
                  <span class="preview-dot" aria-hidden="true">·</span>
                  <span class="preview-live">
                    <span class="live-dot" aria-hidden="true"></span>
                    {{ 'aiVoiceInvoice.previewLive' | translate }}
                  </span>
                </span>
              </div>
            </div>
            <div class="preview-head-actions">
              <button
                type="button"
                class="icon-btn icon-btn--sm"
                (click)="refreshInvoicePreview()"
                [disabled]="previewLoading"
                [title]="'aiVoiceInvoice.previewRefresh' | translate"
                [attr.aria-label]="'aiVoiceInvoice.previewRefresh' | translate"
              >
                <i class="fa fa-refresh" [class.fa-spin]="previewLoading"></i>
              </button>
            </div>
          </header>

          <div class="preview-allocation-bar" *ngIf="showPreviewAllocationBar">
            <label
              class="preview-allocation-label"
              [class.preview-allocation-label--muted]="!previewAllocationEditable"
            >
              <input
                type="checkbox"
                [checked]="previewAllocationChecked"
                [disabled]="!previewAllocationEditable"
                (change)="onPreviewAllocationToggle($event)"
              />
              <span>{{ 'aiVoiceInvoice.previewSendTaxAuthority' | translate }}</span>
              <i
                *ngIf="previewAllocationSaving"
                class="fa fa-spinner fa-spin preview-allocation-spinner"
                aria-hidden="true"
              ></i>
            </label>
            <p class="preview-allocation-hint" *ngIf="previewInvoiceConfirm !== 0">
              {{ 'aiVoiceInvoice.previewAllocationLocked' | translate }}
            </p>
          </div>

          <div class="preview-stage">
            <div class="preview-error" *ngIf="previewError && !previewLoading">
              <span class="preview-error-icon" aria-hidden="true">
                <i class="fa fa-exclamation-triangle"></i>
              </span>
              <p class="preview-error-text">{{ previewError }}</p>
              <button
                type="button"
                class="btn-pill btn-pill--ghost btn-pill--sm"
                (click)="refreshInvoicePreview()"
              >
                <i class="fa fa-refresh"></i>
                {{ 'aiVoiceInvoice.previewRetry' | translate }}
              </button>
            </div>

            <div class="preview-frame-wrap" *ngIf="!previewError">
              <iframe
                class="preview-frame"
                [srcdoc]="previewSafeHtml"
                [title]="'aiVoiceInvoice.previewTitle' | translate"
                sandbox="allow-same-origin allow-popups"
                scrolling="auto"
                (load)="onPreviewFrameLoad($event)"
              ></iframe>

              <div
                class="preview-veil"
                *ngIf="isPreviewBusy"
                [class.preview-veil--soft]="previewSafeHtml != null"
                aria-busy="true"
              >
                <div class="preview-veil-inner">
                  <span class="preview-spinner" aria-hidden="true">
                    <span></span><span></span><span></span>
                  </span>
                  <span class="preview-veil-text">
                    {{
                      (isAiProcessing
                        ? 'aiVoiceInvoice.previewBusyAi'
                        : 'aiVoiceInvoice.previewBusy'
                      ) | translate
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </aside>
        </div>

        <!-- After confirm -->
        <section class="success-card" *ngIf="confirmResult">
          <div class="success-icon" aria-hidden="true">
            <i class="fa fa-check-circle"></i>
          </div>
          <h3 class="success-title">{{ 'aiVoiceInvoice.doneTitle' | translate }}</h3>
          <dl class="success-meta">
            <ng-container *ngIf="confirmResult.document_number">
              <dt>{{ 'aiVoiceInvoice.documentNumber' | translate }}</dt>
              <dd>{{ confirmResult.document_number }}</dd>
            </ng-container>
            <ng-container *ngIf="confirmResult.invoice_id">
              <dt>{{ 'aiVoiceInvoice.invoiceId' | translate }}</dt>
              <dd>{{ confirmResult.invoice_id }}</dd>
            </ng-container>
          </dl>
          <div class="success-actions">
            <button type="button" class="btn-pill btn-pill--primary" (click)="goToInvoiceView()">
              <i class="fa fa-external-link"></i>
              {{ 'aiVoiceInvoice.openInvoice' | translate }}
            </button>
            <a
              *ngIf="confirmResult.pdf_link"
              class="btn-pill btn-pill--light"
              [href]="confirmResult.pdf_link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="fa fa-file-pdf-o"></i>
              {{ 'aiVoiceInvoice.openPdf' | translate }}
            </a>
            <button type="button" class="btn-pill btn-pill--ghost" (click)="resetAfterSuccess()">
              <i class="fa fa-plus-circle"></i>
              {{ 'aiVoiceInvoice.newSession' | translate }}
            </button>
          </div>
        </section>
      </div>
    </section>
  </div>

  <app-add-customer-modal
    [showModal]="showAddCustomerModal"
    mode="add"
    [defaultCompanyId]="addModalCompanyId"
    [suggestedCustomerName]="pendingSuggestedCustomerName"
    [suggestedCustomerDetails]="pendingSuggestedCustomerDetails"
    (onClose)="onAddCustomerModalClose()"
    (onSave)="onCustomerCreatedFromAiModal($event)"
  ></app-add-customer-modal>

  <p-dialog
    [header]="'routes.document.addProduct' | translate"
    [(visible)]="showAddProductModal"
    [modal]="true"
    [style]="{ width: 'min(96vw, 1280px)', 'min-width': '350px' }"
    [contentStyle]="{ maxHeight: 'min(88vh, 860px)', overflow: 'auto' }"
    [draggable]="false"
    [resizable]="false"
    [maximizable]="true"
    [breakpoints]="{ '768px': '100vw' }"
    (onHide)="onAddProductDialogHide()"
    styleClass="add-product-dialog-responsive ai-voice-add-product-dialog"
  >
    <app-add-product-modal
      *ngIf="showAddProductModal"
      [companyId]="selectedCompany?.value ? +selectedCompany.value : null"
      [suggestedProductName]="pendingSuggestedProductName"
      [suggestedProductDetails]="pendingSuggestedProductDetails"
      (productAdded)="onProductCreatedFromAiModal($event)"
      (cancel)="onAddProductDialogHide()"
    ></app-add-product-modal>
  </p-dialog>
</div>
